import { useCallback, useMemo } from 'react';
import _ from 'underscore';
import { odata } from '@bingads-webui/http-util';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { Areas } from './constants';

const getODataConfig = (serverContext) => {
  const isLocal = () => {
    const hostname = document.location.hostname.toLowerCase();
    return hostname.indexOf('ads.microsoft.com') === -1 &&
      hostname.indexOf('ads-int.microsoft.com') === -1;
  };

  const odataPath = isLocal() ? 'AdInsight/V2' : 'AdInsight/V2';

  const oDataConfig = {
    odataToken: serverContext.Token,
    oDataToken: serverContext.Token,
    odataPath: `${serverContext.ServicesEndpoints.AdInsightAPI}${odataPath}`,
    oDataPath: `${serverContext.ServicesEndpoints.AdInsightAPI}${odataPath}`,
  };

  return oDataConfig;
};

export const useDiagnosticsData = ({
  advertiserCustomerId,
  aid,
  campaignId,
  serverContext,
  areas = [Areas.AssetGroup, Areas.Conversion], // Default to AssetGroup and Conversion
}) => {
  const timeZoneId = _.get(serverContext, ['CurrentAccount', 'TimeZone']);

  const oDataConfig = useMemo(() => getODataConfig(serverContext), [serverContext]);
  const oDataOptions = useMemo(() => ({
    contentType: 'application/json',
    path: oDataConfig.odataPath,
    oDataToken: oDataConfig.odataToken,
  }), [oDataConfig]);

  const fetchDiagnosticData = useCallback(() => {
    if (_.get(serverContext, ['DynamicPermissions', 'DiagnosticCMMTApi']) || _.get(serverContext, ['DynamicPermissions', 'ConversionWarningImprovement'])) {
      return edmCampaign
        .Customers.$withKey(advertiserCustomerId)
        .Accounts.$withKey(aid)
        .Campaigns.$withKey(campaignId)
        .Diagnostic['Default.GetCampaignCreationDiagnostics']
        .$makeCall({})
        .toPromise()
        .then(data => data.CampaignDiagnosticDetail);
    }
    const areasStr = areas.map(area => `"${area}"`).join(',');
    const baseURL = `${oDataConfig.odataPath}/Customers(${advertiserCustomerId})/Accounts(${aid})/Campaigns(${campaignId})/Recommendation.GetCampaignCreationDiagnostics(timeZoneId=${timeZoneId},areas=[${areasStr}])`;
    const options = _.extend(
      {},
      oDataOptions,
      {
        url: baseURL,
      }
    );
    const promise = odata.get(options);

    return promise.then(data => data.CampaignDiagnosticDetail);
  }, [advertiserCustomerId, aid, areas, campaignId, oDataConfig.odataPath, oDataOptions, timeZoneId, serverContext]);

  const patchDiagnosticsData = useCallback(({
    diagnosticsData,
    campaignData,
    budgetSuggestionData,
  }) => {
    const patchedDiagnosticsData = _.clone(diagnosticsData);

    const {
      autoGeneratedImageOptOut,
      autoGeneratedTextOptOut,
      finalUrlExpansionOptOut,
    } = campaignData || {};
    if (autoGeneratedImageOptOut || autoGeneratedTextOptOut || finalUrlExpansionOptOut) {
      patchedDiagnosticsData.push({
        Key: Areas.Campaign,
        Value: {
          AuditPoints: ['EnableAutoGeneratedAssets'],
          Status: 'NoAutoGeneratedAssets',
        },
      });
    }

    const enableTcpaCheck = _.get(serverContext, ['DynamicPermissions', 'TargetCpaValueCheck']);
    if (enableTcpaCheck) {
      const {
        Budget: { Amount: BudgetAmount, Type: BudgetType, Status: BudgetStatus } = {},
        BiddingScheme: { Type: BiddingSchemeType, TargetCpa } = {},
      } = campaignData || {};
      if (BudgetType === 'DailyStandard' && BudgetStatus === 'Private' && BiddingSchemeType === 'MaxConversions' && TargetCpa && BudgetAmount < TargetCpa) {
        patchedDiagnosticsData.push({
          Key: Areas.Budget,
          Value: {
            AuditPoints: ['TargetCpaHigherThanBudget'],
            Status: 'InvalidCpa',
          },
        });
      }
    }

    if (budgetSuggestionData) {
      const currentDailyBudget = _.get(campaignData, ['Budget', 'Amount']);
      const suggestedDailyBudget = _.get(budgetSuggestionData, ['Balanced', 'BudgetAmountDaily']);
      if (suggestedDailyBudget && currentDailyBudget < suggestedDailyBudget) {
        patchedDiagnosticsData.push({
          Key: Areas.Budget,
          Value: {
            AuditPoints: ['EstimatedIncreaseInBudget'],
            Status: 'LowerThanSuggestion',
          },
        });
      }
    }

    return patchedDiagnosticsData;
  }, [serverContext]);

  return {
    fetchDiagnosticData,
    patchDiagnosticsData,
  };
};
